<template>
  <div v-if="props.showActivitiesPanel" class="mt-6 bg-gray-50 border border-gray-200 rounded-2xl p-6 shadow-sm relative">
    <!-- Header -->
    <div class="flex items-center justify-between mb-4">
      <h2 class="text-lg font-semibold text-gray-700">Activity Logs <span v-if="!props.isSharepointLogsLoading && fileNameAndTypeText"> of {{
          fileNameAndTypeText }}</span></h2>
      <button @click="closeActivityPanel" class="text-gray-400 hover:text-gray-600 transition-colors"
        aria-label="Close Activity Logs">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>

    <!-- Loading -->
    <div v-if="props.isSharepointLogsLoading" class="flex flex-col items-center justify-center py-8 text-gray-500">
      <svg class="animate-spin h-8 w-8 text-gray-400 mb-3" xmlns="http://www.w3.org/2000/svg" fill="none"
        viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
      </svg>
      <p class="text-base font-medium text-gray-600">Loading activity logs...</p>
      <p class="text-sm text-gray-400 mt-1">Please wait while we fetch the latest updates.</p>
    </div>

    <!-- Logs -->
    <div v-else-if="props.sharePointActivityLogsObject?.logs.length > 0">
      <SharepointFileActivityLog :sharepointActivityLogs="paginatedLogs" :sharepointActivityFile="props.sharePointActivityLogsObject?.file" />

      <!-- Pagination Controls -->
      <div v-if="logsTotalPages > 1" class="flex justify-between items-center mt-6 pt-4 border-t border-gray-200">
        <button @click="prevLogsPage" :disabled="logsCurrentPage === 1"
          class="px-3 py-1 rounded-lg text-sm font-medium border border-gray-300 hover:bg-gray-100 disabled:opacity-50">
          Previous
        </button>

        <div class="text-sm text-gray-600">
          Page {{ logsCurrentPage }} of {{ logsTotalPages }}
        </div>

        <button @click="nextLogsPage" :disabled="logsCurrentPage === logsTotalPages"
          class="px-3 py-1 rounded-lg text-sm font-medium border border-gray-300 hover:bg-gray-100 disabled:opacity-50">
          Next
        </button>
      </div>
    </div>

    <!-- Empty -->
    <div v-else class="flex flex-col items-center justify-center py-8 text-gray-500">
      <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 mb-3 text-gray-400" fill="none" viewBox="0 0 24 24"
        stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
          d="M9 17v-2m3 2v-4m3 4v-6M4 4h16v16H4V4z" />
      </svg>
      <p class="text-base font-medium text-gray-600">No log entries at the moment</p>
      <p class="text-sm text-gray-400 mt-1">Once actions are performed, they’ll appear here.</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { SharepointActivityLog, SharepointActivityLogsObject } from '@/models/SharepointActivityLog';
import { ref,computed } from 'vue';
import SharepointFileActivityLog from './SharepointFileActivityLog.vue';
const sharepointActivityLogs = ref<SharepointActivityLog[]>([])
const logFileNameAndTypeText = ref<String>(null)
const isFileLogsLoading = ref(false)
const logsVisible = ref<Boolean>(false)
const logsCurrentPage = ref(1)
const logsPerPage = 10

interface Props {
  showActivitiesPanel: Boolean;
  isSharepointLogsLoading: Boolean;
  sharePointActivityLogsObject: SharepointActivityLogsObject
  // currentTab?: string;
  // selectedOrderDetails: OrderDetail;
  // isSidepaneLoading: boolean;
  // selectedRow: DashboardItem;
  // filteredOrderDetailTabs: Tab[];
  // currentOrderItem: OrderDetailItem | null;
  // currentOrderItemIndex: number | null;
  // sidebarOpen: boolean;
  // initialTab: string;
  // orderAdmins: OrderAdminUser[];
}

const props = defineProps<Props>();
const emits = defineEmits([
  'close-sharepoint-activity-panel'
]);

// ✅ Pagination logic
// const logsTotalPages = computed(() => Math.ceil(props?.sharePointActivityLogsObject.value?.logs.length / logsPerPage))
const logsTotalPages = computed(() => {
  return Math.ceil(props?.sharePointActivityLogsObject?.logs.length / logsPerPage)
})
const paginatedLogs = computed(() => {
  const start = (logsCurrentPage.value - 1) * logsPerPage
  return props?.sharePointActivityLogsObject?.logs.slice(start, start + logsPerPage)
})

const fileNameAndTypeText = computed(() => {
  return capitalizeFirstLetter(props?.sharePointActivityLogsObject?.file?.type) + ": "+ capitalizeFirstLetter(props?.sharePointActivityLogsObject?.file?.name)
})

const nextLogsPage = () => {
  if (logsCurrentPage.value < logsTotalPages.value) logsCurrentPage.value++
}
const prevLogsPage = () => {
  if (logsCurrentPage.value > 1) logsCurrentPage.value--
}

const closeActivityPanel = () => {
  emits('close-sharepoint-activity-panel')
}

const capitalizeFirstLetter = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}
</script>
